import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { inventoryLocked } from "utils/inventoryLifecycle";
import { withEditableInventory } from "contexts/inventoryPersistence";
import React, { createContext, useEffect, useReducer, useCallback } from "react";
import {
  doc,
  setDoc,
  updateDoc,
  collection,
  query,
  onSnapshot,
  deleteDoc,
  DocumentReference,
  where,
  getDocs,
  getDocFromServer,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { firestore as db, firebaseApp } from "lib/firebase/firebase";
import * as exifr from "exifr";
import dayjs from "dayjs";
import heic2any from "heic2any";

// Project import
import {
  FILES_RESET_STATE,
  FILES_API_CALL_START,
  FILES_API_CALL_FAIL,
  ADDED_FILE_SUCCESS,
  MODIFIED_FILE_SUCCESS,
  REMOVED_FILE_SUCCESS,
  UPLOAD_FILE_SUCCESS,
  DOWNLOAD_FILE_SUCCESS,
  DELETE_FILE_SUCCESS,
} from "store/reducers/actions";
import filesReducer from "store/reducers/files";
import { ERROR_REPORTING_EMAIL } from "config/config-general";
import {
  DeleteFileProps,
  FetchFilesProps,
  FilesContextType,
  UploadFileProps,
  UploadFilesProps,
} from "types/files";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import useAuth from "hooks/useAuth";
import useMapData from "hooks/useMapData";
import { mixpanelEvents } from "config/servicesConfig/mixpanelEvents";
import { dataObjectWithoutUndefinedFields } from "utils/dataManipulation";
import { ENVIRONMENT } from "config/config";

// Firestore event listeners
let firestoreEventListeners: any[] = []; 

const initialState = {
  loading: false,
  error: null,
  orgs: {},
  estates: {},
  maps: {},
  projects: {},
  activities: {},
  inventories: {},
};

const FilesContext = createContext<FilesContextType | null>(null);

export function FilesProvider({ children }: { children: React.ReactNode }) {
  const { isAudienceMode } = useAppFunctionalityConfig();
  if (!isAudienceMode) return <WorkspaceFilesProvider>{children}</WorkspaceFilesProvider>;
  const deny = async () => { throw new Error("audience-read-only"); };
  return <FilesContext.Provider value={{ ...initialState, fetchFiles: deny, uploadFile: deny, uploadFiles: deny, deleteFile: deny, downloadFile: () => { throw new Error("audience-unavailable"); } }}>{children}</FilesContext.Provider>;
}

function WorkspaceFilesProvider({ children }: { children: React.ReactNode }) {
  const { assertDomainMutationAllowed } = useAppFunctionalityConfig();
  const [state, dispatch] = useReducer(filesReducer, initialState);
  const { isLoggedIn, user, callCloudFunction, sendEmail } = useAuth();
  const { selectedMapId, selectedProjectId } = useEstatesAndMaps();
  const { registerFileHandler, unregisterFileHandler } = useMapData();

  // --- Reset state and unsubscribe from firestore listeners --- //
  useEffect(() => {
    return () => {
      dispatch({ type: FILES_RESET_STATE });
      firestoreEventListeners.forEach((unsubscribe) => unsubscribe());
      firestoreEventListeners = [];
    };
  }, []);

  // --- Fetch files based on selected map and project id --- //
  useEffect(() => {
    if (isLoggedIn && selectedMapId && !state.maps[selectedMapId]) {
      fetchFiles({ rootFolder: "maps", rootId: selectedMapId });
    }
  }, [selectedMapId]);

  useEffect(() => {
    if (isLoggedIn && selectedProjectId && !state.projects[selectedProjectId]) {
      fetchFiles({ rootFolder: "projects", rootId: selectedProjectId });
    }
  }, [selectedProjectId]);

  // Callback to delete files for a given activity used to ensure that files are deleted when an activity is deleted
  // even if the user of the app does not have delete permissions for files directly.
  // This function is registered with the MapDataContext and called from there when an activity is deleted.
  const deleteFilesForActivity = useCallback(async (activityId: string) => {
    // Query the files subcollection for this activity
    const filesColRef = collection(db, `activities/${activityId}/files`);
    const snapshot = await getDocs(filesColRef);
    // If nothing to delete, resolve immediately
    if (snapshot.empty) return;
    // Run deletes in parallel but bubble up errors if any fail.
    const deletePromises = snapshot.docs.map(async (fileDoc) => {
      const fileData = fileDoc.data();
      // Call the already implementd deleteFile function to handle storage and firestore deletion
      return deleteFile({
        fileDocId: fileData.id,
        fileName: fileData.name,
        bucket: fileData.bucket,
        rootFolder: "activities",
        rootId: activityId,
      });
    });
    await Promise.all(deletePromises);
  }, []);

  // Register the deleteFilesForActivity callback with the MapDataContext so it can be called when an activity is deleted
  useEffect(() => {
    const key = "filesProvider"; // Unique key for this provider instance
    registerFileHandler(key, deleteFilesForActivity);
    return () => unregisterFileHandler(key);
  }, [registerFileHandler, unregisterFileHandler, deleteFilesForActivity]);

  // --- Fetch files --- //
  const fetchFiles = async ({ rootFolder, rootId }: FetchFilesProps) => {
    dispatch({ type: FILES_API_CALL_START });
    try {
      // Helper to run the same privacy filtering + dispatch logic for a doc change
      const handleFileChange = (changeType: string, data: any) => {
        if (!data) return;
        // Filter according to private/internal categories
        if (data.featureCategories && data.uploadedBy) {
          const { orgId, id } = data.uploadedBy;
          if (data.featureCategories.includes("private") && id !== user?.id) {
            changeType = "removed";
          }
          if (data.featureCategories.includes("internal") && orgId !== user?.orgId) {
            changeType = "removed";
          }
        }
        if (changeType === "added") {
          dispatch({
            type: ADDED_FILE_SUCCESS,
            rootFolder,
            rootId,
            payload: data,
          });
        } else if (changeType === "modified") {
          dispatch({
            type: MODIFIED_FILE_SUCCESS,
            rootFolder,
            rootId,
            payload: data,
          });
        } else if (changeType === "removed") {
          dispatch({
            type: REMOVED_FILE_SUCCESS,
            rootFolder,
            rootId,
            payload: data,
          });
        }
      };

      // 1) Root-level files listener (project-level files or other rootFolder files)
      const rootFilesQuery = query(collection(db, `${rootFolder}/${rootId}/files`));
      const unsubscribeRootFiles = onSnapshot(rootFilesQuery, (qs) => {
        qs.docChanges().forEach((change) => {
          const data = change.doc.data();
          handleFileChange(change.type, data);
        });
      });
      firestoreEventListeners.push(unsubscribeRootFiles);

      // If not a project, nothing more to do
      if (rootFolder !== "projects") return;

      // 2) Activities query (realtime) so we catch newly added activities
      const activityQuery = query(
        collection(db, "activities"),
        where("projectId", "==", rootId),
        where("status", "!=", "deleted"),
      );

      // Map to track per-activity files unsubscribes
      const activityFileUnsubscribes = new Map<string, () => void>();

      // Small helper to remove an unsubscribe from the global array if desired
      const removeUnsubscribeFromGlobal = (unsub: () => void) => {
        const idx = firestoreEventListeners.findIndex((u) => u === unsub);
        if (idx !== -1) firestoreEventListeners.splice(idx, 1);
      };

      const unsubscribeActivities = onSnapshot(activityQuery, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const activityId = change.doc.id;
          const activityData = change.doc.data?.() ?? change.doc.data?.(); // safe access

          // ACTIVITY ADDED: start listening to its files if not already listening
          if (change.type === "added") {
            if (!activityFileUnsubscribes.has(activityId)) {
              const filesQuery = query(collection(db, `activities/${activityId}/files`));
              const unsubFiles = onSnapshot(filesQuery, (filesSnapshot) => {
                filesSnapshot.docChanges().forEach((fileChange) => {
                  const data = fileChange.doc.data();
                  handleFileChange(fileChange.type, data);
                });
              });

              activityFileUnsubscribes.set(activityId, unsubFiles);
              firestoreEventListeners.push(unsubFiles);
            }
          }

          // ACTIVITY REMOVED: unsubscribe file listener and optionally dispatch removals
          if (change.type === "removed") {
            const unsub = activityFileUnsubscribes.get(activityId);
            if (unsub) {
              unsub();
              activityFileUnsubscribes.delete(activityId);
              // Remove the unsub from the global list so cleanup later doesn't call it twice
              removeUnsubscribeFromGlobal(unsub);
            }
            // Optionally: dispatch REMOVED_FILE for files belonging to this activity
            // if you track activityId on files in your store. If you want that, iterate
            // your store and dispatch REMOVED_FILE for items with activityId === activityId.
          }

          // ACTIVITY MODIFIED: handle status changes (e.g. to 'deleted') or other relevant changes
          if (change.type === "modified") {
            const newStatus = change.doc.data()?.status;
            // If activity transitioned to deleted/archived, unsubscribe its files
            if (newStatus === "deleted" || newStatus === "archived") {
              const unsub = activityFileUnsubscribes.get(activityId);
              if (unsub) {
                unsub();
                activityFileUnsubscribes.delete(activityId);
                removeUnsubscribeFromGlobal(unsub);
              }
              // Optionally dispatch REMOVED_FILE for files belonging to this activity
            }
          }
        });
      });

      // keep activities unsubscribe so it can be cleaned up later
      firestoreEventListeners.push(unsubscribeActivities);
    } catch (error) {
      dispatch({ type: FILES_API_CALL_FAIL, error });
    }
  };

  // --- Upload multiple files --- //
  const uploadFiles = async ({ fileDoc, files, bucket, rootFolder, rootId }: UploadFilesProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: FILES_API_CALL_START });
    try {
      await Promise.all(
        files.map(async (file) => {
          return uploadFile({ fileDoc, file, bucket, rootFolder, rootId });
        }),
      );
      dispatch({ type: UPLOAD_FILE_SUCCESS });
    } catch (error) {
      console.error("Error upload files", error);
      dispatch({ type: FILES_API_CALL_FAIL, error });
    }
  };

  // --- upload file --- //
  const uploadFile = async ({ fileDoc, file, bucket, rootFolder, rootId }: UploadFileProps) => {
    assertDomainMutationAllowed();
    if (rootFolder === "inventories") {
      const parent = await getDocFromServer(doc(db, rootFolder, rootId));
      if (!parent.exists() || inventoryLocked(parent.data())) throw new Error("inventory-stale");
    }
    // Check for existing fileDoc and set refence to doc
    const { id } = fileDoc;
    let fileDocRef: DocumentReference;
    if (id) {
      // Update existing fileDoc
      fileDocRef = doc(db, `${rootFolder}/${rootId}/files/${id}`);
    } else {
      // Create new fileDoc
      fileDocRef = doc(collection(db, `${rootFolder}/${rootId}/files`));
    }
    // --- HEIC and HEIF file conversion --- //
    let processedFile = file;
    if (processedFile.type === "image/heic" || processedFile.type === "image/heif") {
      try {
        const arrayBuffer = await processedFile.arrayBuffer();
        const converted = await heic2any({
          blob: processedFile,
          toType: "image/jpeg",
          quality: 0.9,
        });
        // Handle both Blob and Blob[]
        const blob = Array.isArray(converted) ? converted[0] : converted;
        processedFile = new File([blob], processedFile.name.replace(/\.(heic|heif)$/i, ".jpg"), {
          type: "image/jpeg",
        });
      } catch (error) {
        console.error("Failed to convert HEIC/HEIF to JPEG:", error);
      }
    }
    // --- Extract exif data from file if available --- //
    let exifData: { location: { lat: number; lng: number } } = { location: { lat: 0, lng: 0 } };
    if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
      try {
        const exif = await exifr.parse(file, {
          gps: true,
        });
        if (exif && exif.latitude && exif.longitude) {
          exifData = {
            location: { lat: exif.latitude, lng: exif.longitude },
          };
        }
      } catch (error) {
        console.error("Error extracting EXIF data:", error);
      }
    }

    // --- Upload file to firebase storage --- //
    const fileDocId = fileDocRef.id;
    const storage = getStorage(firebaseApp, bucket);
    // Never overwrite bytes referenced by a submission if approval races this upload.
    const version = rootFolder === "inventories" ? `${doc(collection(db, "inventories")).id}:` : "";
    const folderPath = `${rootFolder}/${rootId}/${fileDocId}:${version}${processedFile.name}`;
    const storageRef = ref(storage, folderPath);
    // Create file metadata including the content type
    const metadata = {
      contentType: processedFile.type,
      customMetadata: {
        rootFolder,
        rootId,
        fileDocId,
        lat: exifData.location.lat.toString(),
        lng: exifData.location.lng.toString(),
      },
    };
    // Upload file and metadata to storage ref
    assertDomainMutationAllowed();
    const uploadTask = await uploadBytes(storageRef, processedFile, metadata);
    // Get download url
    const downloadUrl = await getDownloadURL(uploadTask.ref);
    // --- Save file document to firestore --- //
    const fileDocData = {
      ...dataObjectWithoutUndefinedFields(fileDoc),
      type: processedFile.type,
      name: processedFile.name,
      size: processedFile.size,
      id: fileDocId,
      exifData,
      lastModifiedDate: dayjs().format(),
      url: downloadUrl,
    };
    if (rootFolder === "inventories") {
      await withEditableInventory(rootId, async (transaction) => {
        assertDomainMutationAllowed();
        if (id) transaction.update(fileDocRef, fileDocData);
        else transaction.set(fileDocRef, { ...fileDocData, creationDate: dayjs().format() });
      });
      if (!id) callCloudFunction("trackEvent", { event: mixpanelEvents.fileUploaded });
    } else if (id) {
      assertDomainMutationAllowed();
      await updateDoc(fileDocRef, fileDocData);
    } else {
      assertDomainMutationAllowed();
      await setDoc(fileDocRef, {
        ...fileDocData,
        creationDate: dayjs().format(),
      });
      // Log event to mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.fileUploaded });
    }
  };

  // --- download file --- //
  const downloadFile = async (fileUrl: string, fileName?: string) => {
    dispatch({ type: FILES_API_CALL_START });
    try {
      // Create an anchor tag dynamically
      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.download = fileName || "download";
      anchor.target = "_blank"; // Open in a new tab
      anchor.rel = "noopener noreferrer"; // Security measure for opening links in a new tab
      document.body.appendChild(anchor); // Append to the document
      anchor.click(); // Trigger the open in new tab
      document.body.removeChild(anchor); //
      // Log event to mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.fileDownloaded });
      dispatch({ type: DOWNLOAD_FILE_SUCCESS });
    } catch (error) {
      console.error("Download error:", error);
      dispatch({ type: FILES_API_CALL_FAIL, error });
    }
  };

  // --- delete file --- //
  const deleteFile = async ({
    fileDocId,
    fileName,
    bucket,
    rootFolder,
    rootId,
  }: DeleteFileProps) => {
    assertDomainMutationAllowed();
    dispatch({ type: FILES_API_CALL_START });
    try {
      let inventoryFileUrl: string | undefined;
      if (rootFolder === "inventories") {
        const fileRef = doc(db, `${rootFolder}/${rootId}/files/${fileDocId}`);
        inventoryFileUrl = await withEditableInventory(rootId, async (transaction) => {
          assertDomainMutationAllowed();
          const file = await transaction.get(fileRef);
          if (!file.exists()) throw new Error("inventory-stale");
          assertDomainMutationAllowed();
          transaction.delete(fileRef);
          return file.data().url as string | undefined;
        });
      }
      try {
        // --- Delete file from storage --- //
        const storage = getStorage(firebaseApp, bucket);
        const folderPath = `${rootFolder}/${rootId}/${fileDocId}:${fileName}`;
        const storageRef = ref(storage, inventoryFileUrl || folderPath);
        assertDomainMutationAllowed();
        await deleteObject(storageRef);
      } catch (error) {
        sendEmail(
          ERROR_REPORTING_EMAIL,
          `[V2 App Error - ${ENVIRONMENT}] - Error delete file from storage. filename: ${fileName}`,
          `File info:\n\n filename: ${fileName}\n fileDocId: ${fileDocId}\n rootFolder: ${rootFolder}\n rootId: ${rootId}\n\nUser info:\n\nUser id: ${user?.id}\nName: ${user?.firstName} ${user?.lastName}\n\n Error: ${error}`,
        );
      }
      // --- Delete file document from firestore --- //
      const fileDocRef = doc(db, `${rootFolder}/${rootId}/files/${fileDocId}`);
      assertDomainMutationAllowed();
      if (rootFolder !== "inventories") await deleteDoc(fileDocRef);
      // Log event to mixpanel
      callCloudFunction("trackEvent", { event: mixpanelEvents.fileDeleted });
      dispatch({ type: DELETE_FILE_SUCCESS });
    } catch (error) {
      console.error("Error delete file", error);
      dispatch({ type: FILES_API_CALL_FAIL, error });
    }
  };

  return (
    <FilesContext.Provider
      value={{
        ...state,
        fetchFiles,
        uploadFiles,
        uploadFile,
        downloadFile,
        deleteFile,
      }}
    >
      {children}
    </FilesContext.Provider>
  );
}
export default FilesContext;
